import { RouteListener } from '@/services/route';
import {
  Box,
  CircularProgress,
  CssBaseline,
  ThemeProvider,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { ProfileUsers } from './api/enumerations';
import { getLoginLayout } from './api/login';
import { LoginLayoutData } from './api/login/types';
import realPriceLogo from './assets/images/login-realprice-logo.webp';
import smallRealPriceLogo from './assets/images/logo-realprice-menu.svg';
import Menu from './components/Menu';
import { MfaGuard } from './components/MfaGuard';
import ScrollToTop from './components/ScrollToTop';
import { TokenRefresher } from './components/TokenRefresher';
import { getCompanySubdomain } from './helpers';
import { getLocalStorageToken } from './helpers/localStorage';
import { Adjustment } from './pages/Adjustment';
import { Agenda } from './pages/Agenda';
import { Announcements } from './pages/Announcements';
import { AnnouncementForm } from './pages/Announcements/AnnouncementForm';
import { AnnouncementDetails } from './pages/Announcements/Details';
import { Archived } from './pages/Archived';
import { ArchivedFiles } from './pages/ArchivedFiles';
import { Calculation } from './pages/Calculation';
import { CharacteristicsPage } from './pages/Characteristics';
import { Configuration } from './pages/Configuration';
import CreateAccount from './pages/CreateAccount';
import { Dashboard } from './pages/Dashboard';
import { Entrance } from './pages/Entrance';
import { ErrorPage } from './pages/ErrorPage';
import { ErrorReport } from './pages/ErrorReport';
import { ReportDetails } from './pages/ErrorReport/ReportDetails';
import { Finance } from './pages/Finance';
import { ForgotPassword } from './pages/ForgotPassword';
import { Forms } from './pages/Forms';
import { FormCreation } from './pages/Forms/FormCreation';
import { Hive } from './pages/Hive';
import { CompanyInfo } from './pages/Hive/Tabs/CompanyInfo';
import { Licensed } from './pages/Hive/Tabs/Licensed';
import { Notices } from './pages/Hive/Tabs/Notices';
import { AddCompany } from './pages/Hive/Tabs/Notices/AddCompany';
import { LicenseRequest } from './pages/Hive/Tabs/Notices/LicenseRequest';
import { NewNotice } from './pages/Hive/Tabs/Notices/NewNotice';
import { NoticeDetails } from './pages/Hive/Tabs/Notices/NoticeDetails';
import { RedeProto } from './pages/Hive/Tabs/RedeProto';
import { Bsc } from './pages/Hive/Tabs/RedeProto/screens/Bsc';
import { CriticalPoles } from './pages/Hive/Tabs/RedeProto/screens/CriticalPoles';
import { CustomerSuccess } from './pages/Hive/Tabs/RedeProto/screens/CustomerSuccess';
import { Evolution } from './pages/Hive/Tabs/RedeProto/screens/Evolution';
import { Funnel } from './pages/Hive/Tabs/RedeProto/screens/Funnel';
import { Goals } from './pages/Hive/Tabs/RedeProto/screens/Goals';
import { InAccreditation } from './pages/Hive/Tabs/RedeProto/screens/InAccreditation';
import { NetworkMap } from './pages/Hive/Tabs/RedeProto/screens/NetworkMap';
import { Overview } from './pages/Hive/Tabs/RedeProto/screens/Overview';
import { Typologies } from './pages/Hive/Tabs/RedeProto/screens/Typologies';
import { Requests } from './pages/Hive/Tabs/Requests';
import { Home } from './pages/Home';
import { Inspection } from './pages/Inspection';
import { InspectionAddPhotos } from './pages/InspectionPhotos/AddPhotos';
import { ImageDetails } from './pages/InspectionPhotos/ImageDetails';
import { InspectionPhotoGallery } from './pages/InspectionPhotos/PhotoGallery';
import { Rooms } from './pages/InspectionPhotos/Rooms';
import { SelectRooms } from './pages/InspectionPhotos/SelectRooms';
import { Login } from './pages/Login';
import { Mfa } from './pages/Mfa';
import { Notifications } from './pages/Notifications';
import { Oversight } from './pages/Oversight';
import { AdministrativeUsers } from './pages/Oversight/Tabs/AdministrativeUsers';
import { Details } from './pages/Oversight/Tabs/AdministrativeUsers/Details';
import { OversightClients } from './pages/Oversight/Tabs/Clients';
import { CustomerDetails } from './pages/Oversight/Tabs/Clients/CustomerDetails';
import { Pept } from './pages/Pept';
import { PreFlight } from './pages/PreFlight';
import { PresentationMode } from './pages/PresentationMode';
import { Report } from './pages/Report';
import { InspectionPhotos } from './pages/Report/components/InspectionPhotos';
import { ScientificTreatmentSteps } from './pages/Report/ScientificTreatment';
import ResetPassword from './pages/ResetPassword';
import { Revision } from './pages/Revision';
import { SampleCreation } from './pages/SampleCreation';
import { Schedule } from './pages/Schedule';
import { UserManagement } from './pages/UserManagement';
import { Users } from './pages/Users';
import { UsersBankRoles } from './pages/UsersBankRoles';
import PrivateRoute from './PrivateRoute';
import { getTheme } from './styles/theme';

export default function App(): JSX.Element {
  const [loadingLayout, setLoadingLayout] = useState(true);
  const [themeConfig, setThemeConfig] = useState<LoginLayoutData>();
  const companySubdomain = getCompanySubdomain();
  const isRealPrice = companySubdomain.includes('realprice');
  const [token, setToken] = useState(getLocalStorageToken());

  const getCompanyLayout = useCallback(async () => {
    try {
      const layoutResponse = await getLoginLayout();

      if (layoutResponse.detail.description) {
        throw new Error(layoutResponse.detail.description);
      }

      if (layoutResponse.data) {
        setThemeConfig(layoutResponse.data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(error);
      }
    } finally {
      setLoadingLayout(false);
    }
  }, []);

  useEffect(() => {
    getCompanyLayout();
  }, [getCompanyLayout]);
  // eslint-disable-next-line
  useEffect(() => {
    if (isRealPrice) {
      const handleTokenSet = (): void => setToken(getLocalStorageToken());
      window.addEventListener('token-set', handleTokenSet);
      return () => window.removeEventListener('token-set', handleTokenSet);
    }
  }, [isRealPrice]);

  if (loadingLayout) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const theme = getTheme({
    primary: { main: themeConfig?.primary_color || '#004D43' },
    secondary: { main: themeConfig?.secondary_color || '#00B39B' },
  });
  const mainLogo = isRealPrice
    ? realPriceLogo
    : themeConfig?.logo_url || realPriceLogo;
  const smallLogo = isRealPrice
    ? smallRealPriceLogo
    : themeConfig?.small_logo_url || smallRealPriceLogo;
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />

        {token && <TokenRefresher />}
        <RouteListener />

        <Routes>
          <Route path="/" element={<Login logo={mainLogo} />} />
          <Route path="/login" element={<Login logo={mainLogo} />} />
          <Route path="/mfa" element={<Mfa logo={mainLogo} />} />
          <Route
            path="/forgot-password"
            element={<ForgotPassword logo={mainLogo} />}
          />
          <Route
            path="create-account"
            element={<CreateAccount logo={mainLogo} />}
          />
          <Route
            path="new-password"
            element={<ResetPassword logo={mainLogo} />}
          />
          <Route
            path="reset-password"
            element={<ResetPassword logo={mainLogo} />}
          />
          <Route
            element={
              <MfaGuard>
                <Menu smallLogo={smallLogo} />
              </MfaGuard>
            }
          >
            <Route
              path="/home"
              element={
                <PrivateRoute>
                  <Home />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/new"
              element={
                <PrivateRoute>
                  <Entrance />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/edit"
              element={
                <PrivateRoute>
                  <Entrance />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/pept"
              element={
                <PrivateRoute>
                  <Pept />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/schedule"
              element={
                <PrivateRoute>
                  <Schedule />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/inspection"
              element={
                <PrivateRoute>
                  <Inspection />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/inspection/:inspection/rooms"
              element={
                <PrivateRoute>
                  <Rooms />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/inspection/:inspection/rooms/select"
              element={
                <PrivateRoute allowedToBank={false}>
                  <SelectRooms />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/inspection/:inspection/room/:room/add-photos"
              element={
                <PrivateRoute allowedToBank={false}>
                  <InspectionAddPhotos />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/inspection/:inspection/room/:room/gallery"
              element={
                <PrivateRoute>
                  <InspectionPhotoGallery />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/inspection/:inspection/room/:room/image-details/new/:imageId"
              element={
                <PrivateRoute allowedToBank={false}>
                  <ImageDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/inspection/:inspection/room/:room/image-details/:imageId"
              element={
                <PrivateRoute>
                  <ImageDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/sample"
              element={
                <PrivateRoute>
                  <SampleCreation />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/report"
              element={
                <PrivateRoute>
                  <Report />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/report/characteristics"
              element={<CharacteristicsPage />}
            />
            <Route
              path="/home/property/:id/report/scientific-treatment"
              element={<ScientificTreatmentSteps />}
            />
            <Route
              path="/home/property/:id/report/:inspection/inspection-photos"
              element={
                <PrivateRoute allowedToBank={false}>
                  <InspectionPhotos />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/calculation"
              element={
                <PrivateRoute>
                  <Calculation />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/revision"
              element={
                <PrivateRoute>
                  <Revision />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/revision/characteristics"
              element={<CharacteristicsPage />}
            />
            <Route
              path="/home/property/:id/archived"
              element={
                <PrivateRoute>
                  <Archived />
                </PrivateRoute>
              }
            />
            <Route
              path="/home/property/:id/archived/characteristics"
              element={<CharacteristicsPage />}
            />
            <Route
              path="/archived"
              element={
                <PrivateRoute
                  allowedRoles={[ProfileUsers.BANK_RESPONSAVEL_TECNICO]}
                >
                  <ArchivedFiles />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute allowedToBank allowedRoles={[]}>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/adjustment"
              element={
                <PrivateRoute allowedToBank={false}>
                  <Adjustment />
                </PrivateRoute>
              }
            />
            <Route
              path="/users"
              element={
                <PrivateRoute allowedToBank={false}>
                  <Users />
                </PrivateRoute>
              }
            />
            <Route
              path="/users-roles"
              element={
                <PrivateRoute allowedToBank allowedRoles={[]}>
                  <UsersBankRoles />
                </PrivateRoute>
              }
            />
            <Route
              path="/forms"
              element={
                <PrivateRoute allowedToBank>
                  <Forms />
                </PrivateRoute>
              }
            />
            <Route
              path="/forms/new"
              element={
                <PrivateRoute allowedToBank>
                  <FormCreation />
                </PrivateRoute>
              }
            />
            <Route
              path="/forms/:id/edit"
              element={
                <PrivateRoute allowedToBank>
                  <FormCreation />
                </PrivateRoute>
              }
            />
            <Route
              path="pre-flight"
              element={
                <PrivateRoute
                  allowedToUser={false}
                  allowedToBank
                  allowedRoles={[ProfileUsers.BANK_RESPONSAVEL_TECNICO]}
                >
                  <PreFlight />
                </PrivateRoute>
              }
            />
            <Route
              path="the-hive"
              element={
                <PrivateRoute
                  allowedToUser={false}
                  allowedRoles={[
                    ProfileUsers.BANK_RESPONSAVEL_TECNICO,
                    ProfileUsers.CS,
                    ProfileUsers.FINANCEIRO,
                    ProfileUsers.JURIDICO,
                    // pair capacity + analysis queues (PROT-2950): bank
                    // managers and RealPrice engineers own these Hive screens
                    ProfileUsers.BANK_GESTOR_EDITAIS,
                    ProfileUsers.BANK_GESTOR_CREDENCIADO,
                    ProfileUsers.ENGINEER,
                  ]}
                >
                  <Hive />
                </PrivateRoute>
              }
            >
              <Route path="" element={<Requests />} />
              <Route path="requests" element={<Requests />} />
              <Route path="licensed">
                <Route path="" element={<Licensed />} />
                <Route path=":notice" element={<NoticeDetails />} />
                <Route path="company-details/:company">
                  <Route path="" element={<CompanyInfo />} />
                  <Route
                    path="notices/:notice/request/:request"
                    element={<LicenseRequest />}
                  />
                </Route>
              </Route>
              <Route path="notices">
                <Route path="" element={<Notices />} />
                <Route path="new" element={<NewNotice />} />
                <Route path=":notice" element={<NoticeDetails />} />
                <Route path=":notice/edit" element={<NewNotice />} />
                <Route path=":notice/add-company" element={<AddCompany />} />
                <Route
                  path=":notice/request/:request"
                  element={<LicenseRequest />}
                />
              </Route>
              <Route path="rede-proto" element={<RedeProto />}>
                <Route path="" element={<InAccreditation />} />
                <Route path="visao-geral" element={<Overview />} />
                <Route path="bsc" element={<Bsc />} />
                <Route path="mapa" element={<NetworkMap />} />
                <Route path="polos-criticos" element={<CriticalPoles />} />
                <Route path="customer-success" element={<CustomerSuccess />} />
                <Route path="evolucao" element={<Evolution />} />
                <Route path="captacao" element={<Funnel />} />
                <Route path="tipologias" element={<Typologies />} />
                <Route path="metas" element={<Goals />} />
              </Route>
            </Route>
            <Route
              path="/user-management"
              element={
                <PrivateRoute allowedToBank={false}>
                  <UserManagement />
                </PrivateRoute>
              }
            />
            <Route path="/oversight">
              <Route
                element={
                  <PrivateRoute>
                    <Oversight />
                  </PrivateRoute>
                }
              >
                <Route index path="clients" element={<OversightClients />} />
                <Route
                  path="administrative-users"
                  element={<AdministrativeUsers />}
                />
              </Route>
              <Route
                path=":flag/details/:id"
                element={
                  <PrivateRoute>
                    <Details />
                  </PrivateRoute>
                }
              />
              <Route
                path=":flag/customer-details/:id"
                element={
                  <PrivateRoute>
                    <CustomerDetails />
                  </PrivateRoute>
                }
              />
            </Route>
            {isRealPrice && (
              <Route path="/announcements">
                <Route
                  index
                  element={
                    <PrivateRoute allowedToBank={false}>
                      <Announcements />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="new"
                  element={
                    <PrivateRoute allowedToBank={false}>
                      <AnnouncementForm />
                    </PrivateRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <PrivateRoute allowedToBank={false}>
                      <AnnouncementDetails />
                    </PrivateRoute>
                  }
                />
                <Route
                  path=":id/edit"
                  element={
                    <PrivateRoute allowedToBank={false}>
                      <AnnouncementForm />
                    </PrivateRoute>
                  }
                />
              </Route>
            )}
            <Route
              path="/error-report"
              element={
                <PrivateRoute allowedToBank={false}>
                  <ErrorReport />
                </PrivateRoute>
              }
            />
            <Route
              path="/error-report/:id"
              element={
                <PrivateRoute allowedToBank={false}>
                  <ReportDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/agenda"
              element={
                <PrivateRoute allowedToBank={false}>
                  <Agenda />
                </PrivateRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <Notifications />
                </PrivateRoute>
              }
            />
            <Route
              path="/configuration"
              element={
                <PrivateRoute allowedToBank={false}>
                  <Configuration />
                </PrivateRoute>
              }
            />
            <Route
              path="/finance"
              element={
                <PrivateRoute
                  allowedToBank
                  allowedRoles={[ProfileUsers.FINANCEIRO]}
                >
                  <Finance />
                </PrivateRoute>
              }
            />
          </Route>
          <Route
            path="/home/presentation-mode"
            element={
              <PrivateRoute allowedToBank={false}>
                <PresentationMode />
              </PrivateRoute>
            }
          />
          <Route path="*" element={<ErrorPage />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
